<template>
  <div class="amortization-tab">
    <a-row :gutter="16" class="mb-4">
      <a-col :xs="24" :sm="8">
        <a-card size="small" class="kpi-card">
          <div class="kpi-label">
            {{ $t("decaissement.amortizationKpiRemaining") }}
          </div>
          <div class="kpi-value">
            {{ formatAmount(portfolio.totalRemainingBookValue) }}
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="8">
        <a-card size="small" class="kpi-card">
          <div class="kpi-label">
            {{ $t("decaissement.amortizationKpiThisMonth") }}
          </div>
          <div class="kpi-value">
            {{ formatAmount(portfolio.totalDepreciationThisMonth) }}
          </div>
        </a-card>
      </a-col>
      <a-col :xs="24" :sm="8">
        <a-card size="small" class="kpi-card">
          <div class="kpi-label">
            {{ $t("decaissement.amortizationFullyAmortizedCount") }}
          </div>
          <div class="kpi-value">{{ portfolio.fullyAmortizedCount }}</div>
        </a-card>
      </a-col>
    </a-row>

    <div class="filters-section decaissement-filters-stacked">
      <div class="filters-header decaissement-filters-header">
        <h4>
          <a-icon type="filter" class="filter-icon" />
          {{ $t("all.filtresEtRecherche") }}
        </h4>
        <div class="action-button-group">
          <router-link to="/decaissement/nouvelle-depense">
            <a-button type="primary" icon="plus" class="action-button">
              {{ $t("factures.createNewDepense") }}
            </a-button>
          </router-link>
          <a-button class="action-button" @click="resetFilters">
            {{ $t("factures.resetFilters") }}
          </a-button>
          <a-button type="primary" class="action-button" @click="applyFilters">
            {{ $t("action.search") }}
          </a-button>
        </div>
      </div>

      <a-row :gutter="[16, 16]" type="flex" class="decaissement-filters-row">
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="search" class="action-icon" />
              {{ $t("action.search") }}
            </label>
            <a-input
              v-model="filters.search"
              allow-clear
              class="filter-control"
              :placeholder="$t('factures.searchPlaceholder')"
              @pressEnter="applyFilters"
            >
              <a-icon slot="prefix" type="search" />
            </a-input>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="shop" class="action-icon" />
              {{ $t("factures.filterProvider") }}
            </label>
            <a-select
              v-model="filters.provider"
              show-search
              allow-clear
              class="filter-control"
              :filter-option="filterProviderOption"
              :placeholder="$t('factures.filterProvider')"
              :loading="loadingProviders"
              @change="applyFilters"
            >
              <a-select-option
                v-for="p in providers"
                :key="p._id"
                :value="p._id"
              >
                {{ p.fullName || p.company || p._id }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="12" :lg="8" class="filter-col">
          <div class="filter-item filter-item-stack">
            <label class="filter-label">
              <a-icon type="tags" class="action-icon" />
              {{ $t("decaissement.amortizationFilterCategories") }}
            </label>
            <a-select
              v-model="filters.categoryIds"
              mode="multiple"
              allow-clear
              show-search
              :max-tag-count="2"
              class="filter-control"
              option-filter-prop="children"
              :placeholder="
                $t('decaissement.amortizationFilterCategoriesPlaceholder')
              "
              :loading="loadingCategories"
              @change="applyFilters"
            >
              <a-select-option
                v-for="c in capexCategories"
                :key="c._id"
                :value="c._id"
              >
                {{ c.name }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
      </a-row>
    </div>

    <div class="table-container">
      <a-empty
        v-if="!loading && rows.length === 0"
        class="my-4"
        :description="$t('decaissement.amortizationEmpty')"
      />
      <a-table
        v-if="loading || rows.length > 0"
        row-key="_id"
        :loading="loading"
        :columns="columns"
        :data-source="rows"
        :pagination="tablePagination"
        :scroll="{ x: 'max-content' }"
        :custom-row="customRow"
        @change="onTableChange"
      >
        <template slot="label" slot-scope="text">
          <span class="cell-ellipsis">{{ text || "—" }}</span>
        </template>
        <template slot="invoiceRef" slot-scope="text, record">
          {{ record.invoiceNumber || record.reference || "—" }}
        </template>
        <template slot="providerName" slot-scope="text, record">
          {{ record._providerName || "—" }}
        </template>
        <template slot="cost" slot-scope="text, record">
          {{ formatAmount(capexCost(record)) }}
        </template>
        <template slot="remaining" slot-scope="text, record">
          {{
            formatAmount(metrics(record) && metrics(record).remainingBookValue)
          }}
        </template>
        <template slot="thisMonth" slot-scope="text, record">
          {{
            formatAmount(
              metrics(record) && metrics(record).depreciationThisMonth
            )
          }}
        </template>
        <template slot="status" slot-scope="text, record">
          <a-tag :color="statusColor(record)">
            {{ statusLabel(record) }}
          </a-tag>
        </template>
        <template slot="action" slot-scope="text, record">
          <a-button type="link" size="small" @click.stop="openDrawer(record)">
            <a-icon type="eye" />
          </a-button>
        </template>
      </a-table>
    </div>

    <invoice-detail-drawer
      :visible="drawerVisible"
      :depense="selected"
      @close="drawerVisible = false"
      @refresh="loadRows"
    />
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { fetchInvoiceList } from "@/services/api/compV3/decaissement";
import { resolveExpenseKindFromCategory } from "@/utilities/depense/depenseSubcategories";
import { showApiError } from "@/utilities/showApiError";
import InvoiceDetailDrawer from "./components/invoices/InvoiceDetailDrawer.vue";

export default {
  name: "AmortizationTab",
  components: { InvoiceDetailDrawer },
  data() {
    return {
      loading: false,
      loadingProviders: false,
      loadingCategories: false,
      categories: [],
      rows: [],
      providers: [],
      portfolio: {
        totalRemainingBookValue: 0,
        totalDepreciationThisMonth: 0,
        fullyAmortizedCount: 0,
      },
      drawerVisible: false,
      selected: null,
      filters: {
        search: "",
        provider: undefined,
        categoryIds: [],
      },
      pagination: {
        current: 1,
        pageSize: 25,
        total: 0,
      },
      searchDebounce: null,
    };
  },
  computed: {
    capexCategories() {
      const list = this.categories || [];
      return list
        .filter((c) => resolveExpenseKindFromCategory(c) === "CAPEX")
        .slice()
        .sort((a, b) =>
          String(a.name || "").localeCompare(String(b.name || ""), undefined, {
            sensitivity: "base",
          })
        );
    },
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "25", "50", "100"],
        showTotal: (total) => this.$t("factures.paginationTotal", { total }),
      };
    },
    columns() {
      return [
        {
          title: this.$t("factures.tableLabel"),
          dataIndex: "label",
          key: "label",
          ellipsis: true,
          scopedSlots: { customRender: "label" },
        },
        {
          title: this.$t("decaissement.invoiceNumber"),
          key: "invoiceRef",
          scopedSlots: { customRender: "invoiceRef" },
          width: 120,
        },
        {
          title: this.$t("fournisseur.companyName"),
          key: "providerName",
          ellipsis: true,
          scopedSlots: { customRender: "providerName" },
        },
        {
          title: this.$t("decaissement.amortizationTableCost"),
          key: "cost",
          scopedSlots: { customRender: "cost" },
          width: 110,
        },
        {
          title: this.$t("decaissement.amortizationTableRemaining"),
          key: "remaining",
          scopedSlots: { customRender: "remaining" },
          width: 120,
        },
        {
          title: this.$t("decaissement.amortizationTableThisMonth"),
          key: "thisMonth",
          scopedSlots: { customRender: "thisMonth" },
          width: 110,
        },
        {
          title: this.$t("decaissement.amortizationTableStatus"),
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: this.$t("action.voir"),
          key: "action",
          width: 72,
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  watch: {
    "filters.search"() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.pagination.current = 1;
        this.loadRows();
      }, 400);
    },
  },
  created() {
    this.loadProviders();
    this.loadCategories();
    this.loadRows();
  },
  methods: {
    metrics(record) {
      return record && record._capexMetrics;
    },
    capexCost(record) {
      const m = this.metrics(record);
      return m ? m.costBasis : null;
    },
    formatAmount(v) {
      if (v == null || !Number.isFinite(Number(v))) return "—";
      return Number(v).toFixed(3);
    },
    statusColor(record) {
      const m = this.metrics(record);
      if (!m) return "default";
      return m.isFullyAmortized ? "green" : "blue";
    },
    statusLabel(record) {
      const m = this.metrics(record);
      if (!m) return "—";
      return m.isFullyAmortized
        ? this.$t("decaissement.amortizationStatusComplete")
        : this.$t("decaissement.amortizationStatusActive");
    },
    filterProviderOption(input, option) {
      const t =
        option.componentOptions &&
        option.componentOptions.children &&
        option.componentOptions.children[0] &&
        option.componentOptions.children[0].text;
      if (!t) return false;
      return String(t).toLowerCase().indexOf(String(input).toLowerCase()) >= 0;
    },
    buildQueryParams() {
      const params = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
        hasCapexAmortization: true,
      };
      if (this.filters.search && this.filters.search.trim()) {
        params.search = this.filters.search.trim();
      }
      if (this.filters.provider) params.provider = this.filters.provider;
      const catIds = (this.filters.categoryIds || []).filter(Boolean);
      if (catIds.length) params.categories = catIds.map(String).join(",");
      return params;
    },
    applyFilters() {
      this.pagination.current = 1;
      this.loadRows();
    },
    resetFilters() {
      this.filters = { search: "", provider: undefined, categoryIds: [] };
      this.pagination.current = 1;
      this.loadRows();
    },
    async loadCategories() {
      this.loadingCategories = true;
      try {
        const res = await apiClient.get("/compV3/depense/categories");
        this.categories = res.data || [];
      } catch {
        this.categories = [];
      } finally {
        this.loadingCategories = false;
      }
    },
    async loadProviders() {
      this.loadingProviders = true;
      try {
        const res = await apiClient.post("/providers/filter", {
          query: { status: "active" },
        });
        this.providers = res.data || [];
      } catch {
        this.providers = [];
      } finally {
        this.loadingProviders = false;
      }
    },
    async loadRows() {
      this.loading = true;
      this.portfolio = {
        totalRemainingBookValue: 0,
        totalDepreciationThisMonth: 0,
        fullyAmortizedCount: 0,
      };
      try {
        const res = await fetchInvoiceList(this.buildQueryParams());
        const body = res.data || {};
        this.rows = body.data || [];
        this.pagination.total = body.total || 0;
        if (body.capexPortfolio) {
          this.portfolio = {
            totalRemainingBookValue: Number(
              body.capexPortfolio.totalRemainingBookValue || 0
            ),
            totalDepreciationThisMonth: Number(
              body.capexPortfolio.totalDepreciationThisMonth || 0
            ),
            fullyAmortizedCount: Number(
              body.capexPortfolio.fullyAmortizedCount || 0
            ),
          };
        }
      } catch (err) {
        showApiError(this, err);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.loadRows();
    },
    customRow(record) {
      return {
        on: {
          click: () => this.openDrawer(record),
        },
      };
    },
    openDrawer(record) {
      this.selected = record;
      this.drawerVisible = true;
    },
  },
};
</script>

<style scoped>
.kpi-card .kpi-label {
  font-size: 13px;
  color: #595959;
  margin-bottom: 6px;
}
.kpi-card .kpi-value {
  font-size: 20px;
  font-weight: 600;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
